import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { appTheme } from "../../shared/theme/app-theme";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { AuthService } from "./login-service";
import { consumeReturnUrl } from "./return-url";
import { useNavigate } from "react-router";
import { useState } from "react";
import { login, setRoles } from "../../shared/reducers/authentication";
import { useAppDispatch } from "../../shared/store/store";
import jwt from "jsonwebtoken";
import { Link } from "@mui/material";
import { useAlert } from "../../shared/alert-context";
import HttpService from "../../shared/service/http-service";

function mapLoginError(message?: string): string {
  if (!message) return "Ошибка входа";
  const normalized = message.trim().toLowerCase();
  if (
    normalized.includes("invalid user credentials") ||
    normalized.includes("invalid_grant")
  ) {
    return "Неверный логин или пароль";
  }
  if (normalized.includes("account is not fully set up")) {
    return "Учётная запись не настроена";
  }
  if (
    normalized.includes("account disabled") ||
    normalized.includes("user is disabled")
  ) {
    return "Учётная запись отключена";
  }
  if (normalized.includes("temporarily disabled")) {
    return "Учётная запись временно заблокирована";
  }
  return message;
}

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    AuthService.login(data).then(
      (resp) => {
        localStorage.setItem("access_token", resp.data.access_token);
        localStorage.setItem("refresh_token", resp.data.refresh_token);
        localStorage.setItem("username", data.get("username") as string);
        const decodedToken = jwt.decode(resp.data.access_token);

        dispatch(setRoles(decodedToken.realm_access.roles));
        dispatch(login());
        navigate(consumeReturnUrl(), { replace: true });
        HttpService.getLoginInfo().then(
          (resp) => {
            const message = `Последняя попытка входа: ${resp.data.date} 
          IP адрес: ${resp.data.ipAddress}
          Статус: ${
            resp.data.successfully ? "Успешная попытка" : "Неуспешная попытка"
          }
          ${
            resp.data.successfully
              ? ""
              : "Количество неуспешных попыток: " +
                (resp.data.loginErrorNumber ?? 0)
          }`;
            showAlert(message, "info");
          }
        );
      },
      (err) => {
        setError(
          mapLoginError(
            err.response?.data?.error_description ?? err.response?.data?.error
          )
        );
      }
    );
  };

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          backgroundColor: "background.default",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: { xs: 3, sm: 4 },
            py: { xs: 4, sm: 5 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
              gap: 1,
            }}
          >
            <Box
              component="img"
              src="/logo-ru.svg"
              alt="Қаржы орталығы"
              sx={{
                height: 44,
                width: "auto",
                maxWidth: 200,
                objectFit: "contain",
              }}
            />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "text.secondary",
              }}
            >
              ИС ГЗС
            </Typography>
          </Box>
          <Typography
            component="h1"
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: "text.primary",
              mb: 3,
            }}
          >
            Авторизация
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ width: "100%" }}
          >
            <TextField
              margin="dense"
              required
              fullWidth
              size="small"
              id="username"
              label="Логин"
              name="username"
              autoComplete="username"
              autoFocus
              sx={{ mb: 1.5 }}
            />
            <TextField
              margin="dense"
              required
              fullWidth
              size="small"
              name="password"
              label="Пароль"
              type="password"
              id="password"
              autoComplete="current-password"
            />
            {error ? (
              <Typography
                sx={{ color: "error.main", fontSize: 13, mt: 1.5 }}
              >
                {error}
              </Typography>
            ) : null}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                mt: 1,
              }}
            >
              <Link
                href="/reset"
                underline="hover"
                sx={{ fontSize: 13 }}
              >
                Забыли пароль?
              </Link>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 2.5,
                mb: 0.5,
                height: 42,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Войти
            </Button>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
